import React from 'react';

interface BasicInfoTabProps {
  formData: {
    name: string;
    type: string;
    capacity: number;
    description: string;
    status: 'available' | 'maintenance' | 'occupied';
    bookingSettings: {
      defaultDuration: number;
      maxBookingsPerDay: number;
      maxBookingDuration: number;
      minBookingNotice: number;
      maxAdvanceBooking: number;
      autoConfirm: boolean;
    };
  };
  onChange: (data: Partial<BasicInfoTabProps['formData']>) => void;
}

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({ formData, onChange }) => {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Typ</label>
          <select
            value={formData.type}
            onChange={(e) => onChange({ type: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="riding-hall">Reithalle</option>
            <option value="lunging-circle">Longierzirkel</option>
            <option value="outdoor-arena">Außenplatz</option>
            <option value="stable">Stall</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Kapazität</label>
          <input
            type="number"
            min="1"
            value={formData.capacity}
            onChange={(e) => onChange({ capacity: parseInt(e.target.value) })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Beschreibung</label>
          <textarea
            value={formData.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
          <select
            value={formData.status}
            onChange={(e) => onChange({ status: e.target.value as BasicInfoTabProps['formData']['status'] })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="available">Verfügbar</option>
            <option value="maintenance">Wartung</option>
            <option value="occupied">Belegt</option>
          </select>
        </div>
      </div>

      <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-4">Buchungseinstellungen</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Standard-Buchungsdauer (Minuten)
            </label>
            <select
              value={formData.bookingSettings.defaultDuration}
              onChange={(e) => onChange({
                bookingSettings: {
                  ...formData.bookingSettings,
                  defaultDuration: parseInt(e.target.value)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            >
              <option value={30}>30 Minuten</option>
              <option value={60}>60 Minuten</option>
              <option value={90}>90 Minuten</option>
              <option value={120}>120 Minuten</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Maximale Buchungen pro Tag
            </label>
            <input
              type="number"
              min="1"
              value={formData.bookingSettings.maxBookingsPerDay}
              onChange={(e) => onChange({
                bookingSettings: {
                  ...formData.bookingSettings,
                  maxBookingsPerDay: parseInt(e.target.value)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Maximale Buchungsdauer (Minuten)
            </label>
            <input
              type="number"
              min="30"
              step="30"
              value={formData.bookingSettings.maxBookingDuration}
              onChange={(e) => onChange({
                bookingSettings: {
                  ...formData.bookingSettings,
                  maxBookingDuration: parseInt(e.target.value)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Mindestvorlaufzeit (Minuten)
            </label>
            <input
              type="number"
              min="0"
              value={formData.bookingSettings.minBookingNotice}
              onChange={(e) => onChange({
                bookingSettings: {
                  ...formData.bookingSettings,
                  minBookingNotice: parseInt(e.target.value)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Maximaler Buchungsvorlauf (Tage)
            </label>
            <input
              type="number"
              min="1"
              value={formData.bookingSettings.maxAdvanceBooking}
              onChange={(e) => onChange({
                bookingSettings: {
                  ...formData.bookingSettings,
                  maxAdvanceBooking: parseInt(e.target.value)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoConfirm"
              checked={formData.bookingSettings.autoConfirm}
              onChange={(e) => onChange({
                bookingSettings: {
                  ...formData.bookingSettings,
                  autoConfirm: e.target.checked
                }
              })}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
            />
            <label htmlFor="autoConfirm" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Buchungen automatisch bestätigen
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};